import { Component, Input, OnInit, inject } from '@angular/core';
import { DealerClaimType } from '@app/core/enums/dealer-claim-type';
import { OpenDialogService } from '@app/core/services/open-dialog.service';
import { DealerStateSelectors } from '@app/core/states/dealer/dealer-state.selectors';
import { CustomerEquipmentUpgradeAgreement } from '@app/sales/modules/mobilephone/models/equipment-upgrade-agreement.model';
import { ApiValidateAgreementImeiAction } from '@app/sales/modules/mobilephone/states/mobilbytte/mobilbytte.actions';
import { MobilBytteSelectors } from '@app/sales/modules/mobilephone/states/mobilbytte/mobilbytte.selectors';
import {
  ApiMobilephoneFiltersAction,
  ApiMobilephoneSearchAction,
} from '@app/sales/modules/mobilephone/states/mobilephone-search/mobilephone-search.actions';
import { CustomerProductMobilePhoneSelectors } from '@app/sales/states/customer-product-mobilephone/customer-product-mobilephone.selectors';
import { ApiGetOfferingsAction } from '@app/sales/states/offering/offering.actions';
import { IconNames } from '@app/shared/enums/icon-names';
import { select, Store } from '@ngxs/store';
import { take } from 'rxjs';
import { SpinnerComponent } from '../atoms/spinner/spinner.component';
import { AllowSettingsDirective } from '../../directives/allow-settings.directive';
import { NgClass } from '@angular/common';
import { IceAlertComponent } from '../molecules/ice-alert/ice-alert.component';
import { MobilBytteHelperService } from '@app/sales/services/mobilbytte-helper.service';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'app-mobilbytte-bytte-info',
  templateUrl: './mobilbytte-bytte-info.component.html',
  styleUrl: './mobilbytte-bytte-info.component.scss',
  imports: [IceAlertComponent, NgClass, AllowSettingsDirective, SpinnerComponent],
})
export class MobilbytteBytteInfoComponent implements OnInit {
  private store = inject(Store);
  private route = inject(ActivatedRoute);
  private openDialogService = inject(OpenDialogService);
  private mobilBytteHelperService = inject(MobilBytteHelperService);

  @Input() showIcon = true;
  @Input() isFullWidth = false;

  hasMobilBytteProductInCart = select(CustomerProductMobilePhoneSelectors.hasMobilBytteProductInCart);
  mobilBytteAgreementsAvailableForBytteBytte = select(MobilBytteSelectors.mobilBytteAgreementsAvailableForBytteBytte);

  IconNames = IconNames;
  DealerClaimType = DealerClaimType;

  isManualRegistration = false;

  ngOnInit(): void {
    this.isManualRegistration = this.store.selectSnapshot<boolean>(DealerStateSelectors.canUseManualRegistration);
  }

  onMobilBytteBytteClick() {
    this.store.dispatch([new ApiGetOfferingsAction(), new ApiMobilephoneSearchAction(), new ApiMobilephoneFiltersAction()]);
    const mobilBytteAgreementsAvailableForBytteBytte = this.mobilBytteAgreementsAvailableForBytteBytte();
    if (mobilBytteAgreementsAvailableForBytteBytte?.length === 1) {
      const agreement = mobilBytteAgreementsAvailableForBytteBytte[0];
      if (agreement.isValidImei) {
        this.changeMobile(agreement);
      } else {
        this.store
          .dispatch(new ApiValidateAgreementImeiAction(agreement.agreementId))
          .pipe(take(1))
          .subscribe(() => {
            const mobilBytteAgreementAvailableForUpgrade = this.mobilBytteAgreementAvailableForUpgrade;
            if (mobilBytteAgreementAvailableForUpgrade.isValidImei) {
              this.changeMobile(mobilBytteAgreementAvailableForUpgrade);
            }
          });
      }
    }
  }

  changeMobile(agreement: CustomerEquipmentUpgradeAgreement) {
    if (this.mobilBytteHelperService.isV2(agreement)) {
      this.openDialogService.openMobilephoneMobilbytteBytteSearchDialog(
        +agreement.agreementId,
        this.isManualRegistration,
        this.route
      );
    } else {
      const dialogRef = this.openDialogService.openMobilbytteBytteMobilePhoneDialog(agreement.activeImei);

      dialogRef
        .afterClosed()
        .pipe(take(1))
        .subscribe(openMobileSelector => {
          if (openMobileSelector) {
            this.openDialogService.openMobilephoneMobilbytteBytteSearchDialog(
              +agreement.agreementId,
              this.isManualRegistration,
              this.route
            );
          }
        });
    }
  }

  get mobilBytteBytteText(): string {
    if (!!this.mobilBytteAgreementAvailableForUpgrade) {
      return 'Kunden har en MobilBytte-avtale som kan byttes inn.';
    } else {
      return 'Kunden har flere MobilBytte-avtaler som kan byttes inn.';
    }
  }

  get mobilBytteAgreementAvailableForUpgrade(): CustomerEquipmentUpgradeAgreement {
    const mobilBytteAgreementsAvailableForBytteBytte = this.mobilBytteAgreementsAvailableForBytteBytte();
    return mobilBytteAgreementsAvailableForBytteBytte?.length === 1 ? mobilBytteAgreementsAvailableForBytteBytte[0] : null;
  }

  get isValidatingImei(): boolean {
    return this.mobilBytteAgreementAvailableForUpgrade?.isValidatingImei;
  }
}
